import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "@mantine/core";
import {
  IconCalendarOff,
  IconCalendarPlus,
  IconWalk,
} from "@tabler/icons-react";
import { useCalendar } from "../../../../context/CalendarContext";
import { useSalon } from "../../../../context/SalonContext";
import { CalendarEventType, SlotInfo } from "../../../../types/calendar";
import { Employee, SalonService } from "../../../../types/salon";
import {
  CalendarEvent,
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../../types/transactions";
import { generateBookingLink } from "../../../../utils/bookingLinkUtils";
import { getSalonTimezone } from "../../../../utils/timeUtils";
// @ts-ignore — staffUtils.js predates the TS migration
import { isBookableActiveEmployee } from "../../../../utils/staffUtils";
import { countActiveFilterGroups } from "../components/Views/FilterDropdown";
import {
  buildOverridesMap,
  buildResources,
  getSalonNow,
  staffHoursOn,
} from "../components/Views/sharedCalendarUtils";
import { TimeBlockSeed } from "../hooks/useSchedulingActions";
import {
  dateFromKey,
  dayHeaderLabel,
  dayKeyOf,
  FULL_DAY_WINDOW,
  isClosedDay,
  MobileAppt,
  salonHoursFor,
  staffDayHours,
  toMobileAppts,
  weekDays,
  weekHeaderLabel,
  monthHeaderLabel,
  initialsOf,
} from "./grid/geometry";
import { ListView } from "../components/Views";
import {
  MobileDayColumn,
  MobileDayGrid,
  MobileStaffColumn,
  SlotPress,
} from "./grid/MobileDayGrid";
import { MobileWeekGrid, WeekSlotPress } from "./grid/MobileWeekGrid";
import { useInitialScroll } from "./grid/useInitialScroll";
import gridStyles from "./grid/MobileGrid.module.css";
import { GRID_TOKENS } from "./tokens";
import { MobileHeaderBand, MobileView } from "./shell/MobileHeaderBand";
import { MobileNavDrawer } from "../../../NavBar/MobileNavDrawer";
import { MobileFabAction, MobileFabStack } from "./shell/MobileFabStack";
import { MobileWeekProviderBar } from "./shell/MobileWeekProviderBar";
import { MobileDatePickerSheet } from "./sheets/MobileDatePickerSheet";
import { MobileWeekProviderSheet } from "./sheets/MobileWeekProviderSheet";
import {
  MobileFilterSelection,
  MobileFiltersSheet,
} from "./sheets/MobileFiltersSheet";
import {
  MobileAppointmentDetailSheet,
  MobileDetailActions,
} from "./detail/MobileAppointmentDetailSheet";
import { MobileNewAppointmentSheet } from "./booking/MobileNewAppointmentSheet";
import { MobileRebookScreen } from "./booking/MobileRebookScreen";
import { MobileToast, MobileToastState } from "./booking/MobileToast";
import { LINK_COPIED_TOAST } from "./booking/bookingSheetModel";
import type { ToastCopy } from "./booking/bookingSheetModel";
import type { MobileDraftSession } from "./booking/useMobileBookingDraft";
import type { EventFlag } from "../components/Views/EventBlock";
import { MobileFlagsContext } from "./grid/MobileFlagsContext";
import { MobileWalkIns } from "./waitlist/MobileWalkIns";
import type { MobileWalkInsWiring } from "./waitlist/MobileWalkIns";
import type { WalkInFormTarget } from "../waitlist/WalkInForm";
import styles from "./MobileCalendarSurface.module.css";

/** The calendar paths the appointment detail sheet uses. */
export interface MobileDetailWiring extends Omit<
  MobileDetailActions,
  "onRebook"
> {
  /** The one transaction lookup (useTransactionStore.findTransaction). */
  findTransaction: (id: string) => GetTransactionsResponse | undefined;
  /** Refetch transactions into the store (after a reschedule). */
  refetchTransactions: (transactionIds: string[]) => void;
}

/** The booking paths the new-appointment sheet and rebook screen use. */
export interface MobileBookingWiring {
  /** The shared booking session (useBookingSession); its draft is the sheet's. */
  session: MobileDraftSession;
  /** Store created transactions (rebook) into the calendar store. */
  storeCreated: (created: TransactionResponse[]) => void;
  /**
   * The appointment the pushed rebook screen is for (null = closed). Owned
   * by CalendarView so Appointment Details' Rebook can open it too.
   */
  rebookTxn: GetTransactionsResponse | null;
  setRebookTxn: (txn: GetTransactionsResponse | null) => void;
}

export interface MobileCalendarSurfaceProps {
  /** Events after staff / Filters / calendar.scope filtering (rendered). */
  events: CalendarEventType[];
  /** Events after calendar.scope only (Filters counts + date dots). */
  scopedEvents: CalendarEventType[];
  /** Time-block tap → the existing time-block edit modal. */
  onTimeBlockTap: (event: CalendarEventType) => void;
  /** Appointment tap → the detail sheet, wired to these calendar paths. */
  detail: MobileDetailWiring;
  /** New appointment sheet + rebook screen. */
  booking: MobileBookingWiring;
  onNewAppointment: () => void;
  onNewTimeBlock: () => void;
  /** The walk-in waitlist store (CalendarView's) + its assign follow-up. */
  walkIns: MobileWalkInsWiring;
  onSlotAppointment: (slot: SlotInfo) => void;
  onSlotTimeBlock: (seed: TimeBlockSeed) => void;
  /** Rendered above the header band (trial banner). */
  banner?: React.ReactNode;
  /** The store's transactions (the List view). */
  transactions?: GetTransactionsResponse[];
  /** Flag chips on grid blocks (index `flagsForEvent`). */
  flagsForEvent?: (event: CalendarEventType) => EventFlag[];
}

type Sheet = "date" | "filters" | "provider" | null;

const WHITE_ICON = { size: 24, stroke: 1.8 } as const;

const toColumn = (r: {
  id: string;
  firstName: string;
  lastName: string;
  avatar?: string | null;
}): MobileStaffColumn => ({
  id: r.id,
  name: `${r.firstName} ${r.lastName}`.trim(),
  initials: initialsOf(r.firstName, r.lastName),
  photo: r.avatar,
});

/**
 * The phone calendar (isMobile): header band, nav drawer, day / week grids,
 * FAB stack, and the date / filter sheets. Data and filters come from the
 * same hooks and CalendarContext as desktop.
 */
export const MobileCalendarSurface: React.FC<MobileCalendarSurfaceProps> = ({
  events,
  scopedEvents,
  onTimeBlockTap,
  detail,
  booking,
  onNewAppointment,
  onNewTimeBlock,
  walkIns,
  onSlotAppointment,
  onSlotTimeBlock,
  banner,
  transactions = [],
  flagsForEvent,
}) => {
  const {
    viewType,
    setViewType,
    selectedDate,
    setSelectedDate,
    filteredEmployees,
    selectedArtists,
    setSelectedArtists,
    selectedServiceTypes,
    setSelectedServiceTypes,
    selectedStatuses,
    setSelectedStatuses,
    workingToday,
    setWorkingToday,
    workingStaffIds,
    hoursOverrides,
  } = useCalendar();
  const { businessInfo, employees, services, serviceTypeOptions, currentUser } =
    useSalon() as {
      businessInfo: (Record<string, unknown> & { id?: string }) | null;
      employees: Employee[] | undefined;
      services: SalonService[] | undefined;
      serviceTypeOptions: string[] | undefined;
      /** The signed-in user's staff record. */
      currentUser: { id?: string } | null | undefined;
    };
  const timezone = businessInfo?.timezone as string | undefined;

  const [navOpen, setNavOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(null);
  // Appointment the detail sheet is about (null = closed).
  const [detailFor, setDetailFor] = useState<MobileAppt | null>(null);
  // New / Edit walk-in full-screen form (null = closed).
  const [walkInForm, setWalkInForm] = useState<WalkInFormTarget | null>(null);
  // The waitlist bottom sheet (header Walk-ins icon).
  const [walkInsOpen, setWalkInsOpen] = useState(false);
  // Rebook (detail sheet / Appointment Details): the pushed rebook screen.
  const { rebookTxn, setRebookTxn } = booking;
  // Completed-write toast (new appointment / rebook).
  const [toast, setToast] = useState<MobileToastState | null>(null);
  const toastSeq = useRef(0);
  const showToast = (copy: ToastCopy) => {
    toastSeq.current += 1;
    setToast({ ...copy, id: toastSeq.current });
  };
  // Provider week view shows: picked from the week's provider sheet or a
  // staff-header tap in day view (null = the default, see weekProvider).
  const [weekProviderId, setWeekProviderId] = useState<string | null>(null);

  // Salon wall-clock "now", refreshed each minute (now line, due chips).
  const [now, setNow] = useState(() => getSalonNow(timezone));
  useEffect(() => {
    setNow(getSalonNow(timezone));
    const id = window.setInterval(
      () => setNow(getSalonNow(timezone)),
      60 * 1000,
    );
    return () => window.clearInterval(id);
  }, [timezone]);

  const view: MobileView =
    viewType === "week" ? "week" : viewType === "list" ? "list" : "day";
  const todayKey = dayKeyOf(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const selectedKey = dayKeyOf(selectedDate);

  // ── Staff ────────────────────────────────────────────────────────────
  const bookableStaff = useMemo(
    () => ((employees || []) as Employee[]).filter(isBookableActiveEmployee),
    [employees],
  );
  const allStaff = useMemo(
    () => buildResources(bookableStaff).map(toColumn),
    [bookableStaff],
  );
  const selectedResources = useMemo(
    () => buildResources(filteredEmployees),
    [filteredEmployees],
  );
  const selectedStaff = useMemo(
    () => selectedResources.map(toColumn),
    [selectedResources],
  );

  // ── Appointments ─────────────────────────────────────────────────────
  // A multi-day time block yields one piece per day, so the day and week
  // filters below (by `dayKey`) show it on every day it covers.
  const appts = useMemo(() => toMobileAppts(events, now), [events, now]);
  const scopedAppts = useMemo(
    () => toMobileAppts(scopedEvents, now),
    [scopedEvents, now],
  );

  const days = useMemo(
    () =>
      weekDays(selectedDate).map((date) => ({
        date,
        closed: isClosedDay(businessInfo, date),
      })),
    [selectedDate, businessInfo],
  );
  const weekKeys = useMemo(
    () => new Set(days.map((d) => dayKeyOf(d.date))),
    [days],
  );

  // "Working only" is the shared CalendarContext `workingToday`: providers with
  // working hours on the viewed date (same rule as desktop's "Working today").
  // Each column carries that provider's hours on the viewed date (ad-hoc
  // override, else weekly schedule), the same rule the desktop day view
  // shades by. CalendarContext fetches the overrides for the day view.
  const overridesMap = useMemo(
    () => buildOverridesMap(hoursOverrides),
    [hoursOverrides],
  );
  const dayColumns = useMemo(
    (): MobileDayColumn[] =>
      selectedResources
        .filter((r) => !workingToday || workingStaffIds.has(r.id))
        .map((r) => ({
          ...toColumn(r),
          hours: staffDayHours(staffHoursOn(r, selectedDate, overridesMap)),
        })),
    [
      workingToday,
      workingStaffIds,
      selectedResources,
      selectedDate,
      overridesMap,
    ],
  );

  const dayAppts = useMemo(() => {
    const ids = new Set(dayColumns.map((c) => c.id));
    return appts.filter((a) => a.dayKey === selectedKey && ids.has(a.staffId));
  }, [appts, dayColumns, selectedKey]);

  // Week view shows one provider: the picked one, else the signed-in user,
  // else the first in the salon's staff order (each only while on the
  // calendar). The provider bar names them, so an empty week never reads as
  // "no appointments" when it is someone else's week.
  const selfId = currentUser?.id;
  const weekCandidates = useMemo(
    () =>
      workingToday
        ? selectedStaff.filter((s) => workingStaffIds.has(s.id))
        : selectedStaff,
    [selectedStaff, workingToday, workingStaffIds],
  );
  const weekProvider = useMemo(
    () =>
      weekCandidates.find((s) => s.id === weekProviderId) ||
      weekCandidates.find((s) => s.id === selfId) ||
      weekCandidates[0] ||
      null,
    [weekCandidates, weekProviderId, selfId],
  );

  const weekAppts = useMemo(
    () =>
      weekProvider
        ? appts.filter(
            (a) => a.staffId === weekProvider.id && weekKeys.has(a.dayKey),
          )
        : [],
    [appts, weekProvider, weekKeys],
  );

  // ── Geometry ─────────────────────────────────────────────────────────
  const window_ = FULL_DAY_WINDOW;
  // Salon hours only pick the initial scroll (desktop opens at the salon's
  // opening too); the day grid shades by each provider's own hours.
  const salonHours = salonHoursFor(businessInfo, selectedDate);

  const clampToWindow = (m: number) =>
    Math.min(window_.endMin, Math.max(window_.startMin, m));
  const nowHour = Math.floor(nowMin / 60) * 60;
  const dayScrollMin = clampToWindow(
    selectedKey === todayKey ? nowHour : (salonHours?.open ?? window_.startMin),
  );
  const weekScrollMin = clampToWindow(
    weekKeys.has(todayKey) ? nowHour : (salonHours?.open ?? window_.startMin),
  );

  // ONE scroll container for both views (rendered below, same element type
  // and position), so Day <-> Week keeps the DOM node and its scrollTop: the
  // grids share the 12 AM-based full-day window and px/min, so the same
  // scrollTop is the same time of day. It scrolls to the target only when the
  // viewed day changes, never on a view switch.
  const scrollRef = useRef<HTMLDivElement>(null);
  const ppm = view === "week" ? GRID_TOKENS.ppmWeek : GRID_TOKENS.ppmDay;
  useInitialScroll(
    scrollRef,
    ((view === "week" ? weekScrollMin : dayScrollMin) - window_.startMin) * ppm,
    `${selectedKey}|${ppm}`,
  );

  // ── Filters ──────────────────────────────────────────────────────────
  const filterCount = countActiveFilterGroups(
    selectedArtists,
    selectedServiceTypes,
    selectedStatuses,
  );
  const serviceTypes = useMemo(() => {
    const color: Record<string, string> = {};
    (services || []).forEach((s) => {
      if (
        s.service_type &&
        s.default_calendar_event_color &&
        !color[s.service_type]
      ) {
        color[s.service_type] = s.default_calendar_event_color;
      }
    });
    return (serviceTypeOptions || []).map((name) => ({
      name,
      color: color[name] || "#adb5bd",
    }));
  }, [services, serviceTypeOptions]);

  const filterSelection: MobileFilterSelection = {
    selectedArtists,
    selectedServiceTypes,
    selectedStatuses,
    workingOnly: workingToday,
  };
  const scopedDayAppts = useMemo(() => {
    const ids = new Set(allStaff.map((s) => s.id));
    return scopedAppts.filter(
      (a) => a.dayKey === selectedKey && ids.has(a.staffId),
    );
  }, [scopedAppts, selectedKey, allStaff]);
  const applyFilters = (next: MobileFilterSelection) => {
    setSelectedArtists(next.selectedArtists);
    setSelectedServiceTypes(next.selectedServiceTypes);
    setSelectedStatuses(next.selectedStatuses);
    setWorkingToday(next.workingOnly);
  };

  const busyDays = useMemo(() => {
    const keys = new Set<string>();
    scopedAppts.forEach((a) => {
      if (!a.isBlock) keys.add(a.dayKey);
    });
    return keys;
  }, [scopedAppts]);
  const isClosed = useCallback(
    (date: Date) => isClosedDay(businessInfo, date),
    [businessInfo],
  );

  // ── Actions ──────────────────────────────────────────────────────────
  const slotStart = (day: Date, startMin: number) =>
    new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, startMin);

  const slotAppointment = (day: Date, staffId: string, startMin: number) => {
    const start = slotStart(day, startMin);
    onSlotAppointment({
      start,
      end: new Date(start.getTime() + 30 * 60 * 1000),
      resourceId: staffId,
      action: "click",
    });
  };
  const slotTimeBlock = (day: Date, staffId: string, startMin: number) =>
    onSlotTimeBlock({
      startTime: `${String(Math.floor(startMin / 60)).padStart(2, "0")}:${String(startMin % 60).padStart(2, "0")}`,
      resourceId: staffId,
      date: day,
    });
  const handleSlotAppointment = ({ staffId, startMin }: SlotPress) =>
    slotAppointment(selectedDate, staffId, startMin);
  const handleSlotTimeBlock = ({ staffId, startMin }: SlotPress) =>
    slotTimeBlock(selectedDate, staffId, startMin);
  // Week view: the week's provider, on the pressed column's day.
  const handleWeekSlotAppointment = ({ dayKey, startMin }: WeekSlotPress) => {
    if (weekProvider)
      slotAppointment(dateFromKey(dayKey), weekProvider.id, startMin);
  };
  const handleWeekSlotTimeBlock = ({ dayKey, startMin }: WeekSlotPress) => {
    if (weekProvider)
      slotTimeBlock(dateFromKey(dayKey), weekProvider.id, startMin);
  };

  // ── Detail sheet ─────────────────────────────────────────────────────
  const { findTransaction, refetchTransactions } = detail;
  // While a scheduling surface is up (rebook screen, new-appointment sheet,
  // or the detail sheet incl. its reschedule section) a block tap does
  // nothing: no second detail sheet over the draft. (The sheets' scrims
  // normally catch the tap; this is the explicit rule.)
  const schedulingOpen =
    !!rebookTxn || booking.session.sheetOpen || !!detailFor;
  const handleBlockTap = (a: MobileAppt) => {
    if (schedulingOpen) return;
    if (a.isBlock) {
      onTimeBlockTap(a.event);
      return;
    }
    const txnId = (a.event as CalendarEvent).transactionId;
    if (txnId && findTransaction(txnId)) setDetailFor(a);
  };
  // Live values: the store's transaction and the regenerated event (a status
  // change or refetch replaces both), falling back to what was tapped.
  const detailAppt = detailFor
    ? scopedAppts.find((a) => a.id === detailFor.id) || detailFor
    : null;
  const detailTxn = detailAppt
    ? findTransaction((detailAppt.event as CalendarEvent).transactionId) || null
    : null;
  const detailActions: MobileDetailActions = {
    ...detail,
    onView: (txn) => {
      setDetailFor(null);
      detail.onView(txn);
    },
    onRebook: (txn) => {
      setDetailFor(null);
      setRebookTxn(txn);
    },
  };
  // List view: a row opens the detail sheet (its block in the loaded
  // events), else Appointment Details.
  const openFromList = (txn: GetTransactionsResponse) => {
    if (schedulingOpen) return;
    const appt = scopedAppts.find(
      (a) =>
        !a.isBlock &&
        (a.event as CalendarEvent).transactionId === txn.transaction.id,
    );
    if (appt) setDetailFor(appt);
    else detail.onView(txn);
  };

  const handleRescheduled = (day: string, transactionIds: string[]) => {
    setDetailFor(null);
    setSelectedDate(dateFromKey(day));
    if (transactionIds.length) refetchTransactions(transactionIds);
  };

  const openDay = (date: Date) => {
    setSelectedDate(date);
    setViewType("day");
  };

  const fabActions: MobileFabAction[] = [
    {
      key: "new-appointment",
      label: "New Appointment",
      color: "#36afc9",
      icon: <IconCalendarPlus {...WHITE_ICON} />,
      onSelect: onNewAppointment,
    },
    {
      key: "time-block",
      label: "Time block",
      color: "#e08b4f",
      icon: <IconCalendarOff {...WHITE_ICON} />,
      onSelect: onNewTimeBlock,
    },
    {
      key: "walk-in",
      label: "Walk-in",
      color: "#9ccc5a",
      icon: <IconWalk {...WHITE_ICON} />,
      // Opens the New walk-in screen directly (no list first).
      onSelect: () => setWalkInForm({ mode: "new" }),
    },
  ];

  return (
    <MobileFlagsContext.Provider value={flagsForEvent}>
      <Box className={styles.root} data-testid="mobile-calendar-surface">
        {banner}
        <MobileHeaderBand
          dateLabel={
            view === "week"
              ? weekHeaderLabel(days[0].date)
              : view === "list"
                ? monthHeaderLabel(selectedDate)
                : dayHeaderLabel(selectedDate)
          }
          view={view}
          filterCount={filterCount}
          bookingLink={generateBookingLink(businessInfo?.id)}
          onLinkCopied={() => showToast(LINK_COPIED_TOAST)}
          onMenu={() => setNavOpen(true)}
          onDate={() => setSheet("date")}
          onView={setViewType}
          onFilters={() => setSheet("filters")}
          walkIns={{
            count: walkIns.store.count,
            onOpen: () => setWalkInsOpen(true),
          }}
        />

        {view === "week" && weekProvider && (
          <MobileWeekProviderBar
            provider={weekProvider}
            isSelf={weekProvider.id === selfId}
            onOpen={() => setSheet("provider")}
          />
        )}
        {view === "list" && (
          <Box className={styles.list} data-testid="mobile-list-view">
            <ListView
              transactions={transactions}
              onSelectTransaction={openFromList}
            />
          </Box>
        )}
        <Box
          ref={scrollRef}
          className={gridStyles.scroll}
          data-testid="mobile-grid-scroll"
          hidden={view === "list"}
        >
          {view === "day" ? (
            <MobileDayGrid
              columns={dayColumns}
              appts={dayAppts}
              window={window_}
              nowMin={selectedKey === todayKey ? nowMin : null}
              onBlockTap={handleBlockTap}
              onStaffHead={setWeekProviderId}
              onSlotAppointment={handleSlotAppointment}
              onSlotTimeBlock={handleSlotTimeBlock}
            />
          ) : (
            <MobileWeekGrid
              days={days}
              appts={weekAppts}
              window={window_}
              selectedKey={selectedKey}
              todayKey={todayKey}
              nowMin={nowMin}
              onBlockTap={handleBlockTap}
              onOpenDay={openDay}
              onSlotAppointment={
                weekProvider ? handleWeekSlotAppointment : undefined
              }
              onSlotTimeBlock={
                weekProvider ? handleWeekSlotTimeBlock : undefined
              }
            />
          )}
        </Box>

        <MobileFabStack
          open={fabOpen}
          onToggle={() => setFabOpen((o) => !o)}
          onClose={() => setFabOpen(false)}
          actions={fabActions}
        />

        <MobileNavDrawer opened={navOpen} onClose={() => setNavOpen(false)} />
        <MobileDatePickerSheet
          opened={sheet === "date"}
          onClose={() => setSheet(null)}
          selectedDate={selectedDate}
          todayKey={todayKey}
          busyDays={busyDays}
          isClosed={isClosed}
          onSelectDate={setSelectedDate}
        />
        <MobileWeekProviderSheet
          opened={sheet === "provider"}
          onClose={() => setSheet(null)}
          options={weekCandidates}
          selectedId={weekProvider?.id ?? null}
          onPick={setWeekProviderId}
        />
        <MobileFiltersSheet
          opened={sheet === "filters"}
          onClose={() => setSheet(null)}
          staff={allStaff}
          serviceTypes={serviceTypes}
          selection={filterSelection}
          dayAppts={scopedDayAppts}
          workingStaffIds={workingStaffIds}
          onApply={applyFilters}
        />
        <MobileAppointmentDetailSheet
          appt={detailAppt}
          transaction={detailTxn}
          timezone={getSalonTimezone({ timezone })}
          events={scopedEvents}
          staff={bookableStaff}
          actions={detailActions}
          onClose={() => setDetailFor(null)}
          onRescheduled={handleRescheduled}
        />
        <MobileNewAppointmentSheet
          session={booking.session}
          services={services || []}
          staff={bookableStaff}
          events={scopedEvents}
          timezone={getSalonTimezone({ timezone })}
          onBooked={showToast}
        />
        {rebookTxn && (
          <MobileRebookScreen
            transaction={rebookTxn}
            services={services || []}
            timezone={getSalonTimezone({ timezone })}
            events={scopedEvents}
            onClose={() => setRebookTxn(null)}
            onRebooked={(created, day, copy) => {
              booking.storeCreated(created);
              setRebookTxn(null);
              setSelectedDate(dateFromKey(day));
              showToast(copy);
            }}
          />
        )}
        <MobileWalkIns
          store={walkIns.store}
          onAssigned={walkIns.onAssigned}
          timezone={getSalonTimezone({ timezone })}
          services={services || []}
          sheetOpen={walkInsOpen}
          onSheetClose={() => setWalkInsOpen(false)}
          formTarget={walkInForm}
          onFormTargetChange={setWalkInForm}
          onToast={showToast}
        />
        <MobileToast toast={toast} onDismiss={() => setToast(null)} />
      </Box>
    </MobileFlagsContext.Provider>
  );
};
